import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Search, User, Heart, Clock, BookmarkPlus, LogOut, Menu, X, Crown, MessageCircle, Video, Star, Radio, Wand2 } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/browse/straight', label: 'Straight', color: 'text-pink-400' },
    { href: '/browse/gay', label: 'Gay', color: 'text-blue-400' },
    { href: '/browse/trans', label: 'Trans', color: 'text-cyan-400' },
    { href: '/live', label: 'Live', color: 'text-red-500', icon: Radio },
    { href: '/ai-generator', label: 'AI Art', color: 'text-purple-400', icon: Wand2 },
    { href: '/creators', label: 'Creators' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <span className="text-2xl md:text-3xl font-normal tracking-wider uppercase text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Velvet<span className="text-gray-400">Stream</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm tracking-wider uppercase flex items-center gap-1 ${location.pathname === link.href ? 'active' : ''} ${link.color || ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.icon && <link.icon className="w-3 h-3" />}
                {link.label}
              </Link>
            ))}
          </div>

          {/* Search & User */}
          <div className="flex items-center gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-48 lg:w-64 bg-[#1A1A1A] border border-white/10 rounded-sm px-4 py-2 pl-10 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none transition-all"
                  data-testid="search-input"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </form>

            {/* Mobile Search Icon */}
            <button 
              className="md:hidden p-2 text-gray-400 hover:text-white"
              onClick={() => navigate('/search')}
              data-testid="mobile-search-btn"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-2 rounded-sm hover:bg-white/5 transition-colors" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-white/10">
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-sm font-medium text-white">{user?.username}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate('/watchlist')}
                    className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                    data-testid="menu-watchlist"
                  >
                    <BookmarkPlus className="w-4 h-4 mr-3" />
                    My Watchlist
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/favorites')}
                    className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                    data-testid="menu-favorites"
                  >
                    <Heart className="w-4 h-4 mr-3" />
                    Favorites
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/history')}
                    className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                    data-testid="menu-history"
                  >
                    <Clock className="w-4 h-4 mr-3" />
                    Watch History
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {user?.is_creator && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/creator-studio')}
                      className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                      data-testid="menu-creator-studio"
                    >
                      <Video className="w-4 h-4 mr-3" />
                      Creator Studio
                    </DropdownMenuItem>
                  )}
                  {(user?.subscription_tier === 'creator' || user?.subscription_tier === 'model') && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/messages')}
                      className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                      data-testid="menu-messages"
                    >
                      <MessageCircle className="w-4 h-4 mr-3" />
                      Messages
                    </DropdownMenuItem>
                  )}
                  {!user?.subscription_tier && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/subscription')}
                      className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                      data-testid="menu-upgrade"
                    >
                      <Crown className="w-4 h-4 mr-3" />
                      Go Premium
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-gray-400 hover:text-white"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-white text-black hover:bg-gray-200 font-semibold tracking-wide uppercase text-sm"
                  data-testid="register-btn"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 text-gray-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {!isAuthenticated && (
                <>
                  <hr className="border-white/10 my-2" />
                  <Link
                    to="/login"
                    className="px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-3 text-white bg-white/10 hover:bg-white/20 rounded-sm transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
